import type {
  AgentCostBreakdown,
  AgentProfile,
  CostBreakdown,
  CostLine,
  ModelPricing,
  PricingPayload,
  ProviderCachingConfig,
  SimulationParams,
} from '../types';

// ─────────────────────────────────────────────────────────────────────────────
// Pure cost engine. All inputs from (params, pricing). Every line item carries
// a `formula` string so the UI can show a receipt-style explanation. The
// formula is constructed from the SAME values used in the cost calculation —
// they cannot drift.
// ─────────────────────────────────────────────────────────────────────────────

const ZERO: CostLine = { cost: 0, formula: '0' };

function fmtNum(n: number): string {
  if (!isFinite(n)) return '0';
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1_000) return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
  if (Math.abs(n) < 0.01 && n !== 0) return n.toExponential(2);
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function lookupModel(pricing: PricingPayload, modelId: string): ModelPricing {
  return pricing.models.find(m => m.id === modelId) ?? pricing.models[0];
}

function providerCachingFor(pricing: PricingPayload, provider: string): ProviderCachingConfig {
  switch (provider) {
    case 'anthropic': return pricing.simulator.anthropic_caching;
    case 'openai':    return pricing.simulator.openai_caching;
    case 'google':    return pricing.simulator.google_caching;
    case 'moonshot':  return pricing.simulator.moonshot_caching;
    default:          return pricing.simulator.google_caching;
  }
}

function cacheHitPctFor(pricing: PricingPayload, provider: string): number {
  const a = pricing.simulator.history_caching_assumptions;
  switch (provider) {
    case 'anthropic': return a.anthropic_cache_hit_pct_after_turn_2;
    case 'openai':    return a.openai_cache_hit_pct_after_turn_2;
    case 'google':    return a.google_cache_hit_pct_after_turn_2;
    case 'moonshot':  return a.moonshot_cache_hit_pct_after_turn_2;
    default:          return 0;
  }
}

function chunksPerFile(pricing: PricingPayload, avgFileSizeMb: number): number {
  const charsPerMb = 1_000_000 / 4;
  const totalChars = avgFileSizeMb * charsPerMb;
  return Math.min(
    pricing.simulator.embeddings.max_chunks_per_file,
    Math.max(1, Math.ceil(totalChars / pricing.simulator.embeddings.chars_per_chunk)),
  );
}

function haikuModel(pricing: PricingPayload): ModelPricing {
  return lookupModel(pricing, 'claude-haiku-4-5-20251001');
}

function enrichmentModel(pricing: PricingPayload): ModelPricing {
  return lookupModel(pricing, pricing.simulator.file_enrichment.model);
}

// ── Per-agent calculation ───────────────────────────────────────────────────

function calculateAgentCost(
  agent: AgentProfile,
  params: SimulationParams,
  pricing: PricingPayload,
): AgentCostBreakdown {
  const model = lookupModel(pricing, agent.model);
  const caching = providerCachingFor(pricing, model.provider);
  const cacheHitPct = cacheHitPctFor(pricing, model.provider);

  const convsPerInstance = agent.activeUsersPerMonth * agent.conversationsPerUserPerMonth;
  const totalConversations = convsPerInstance * params.numSubTenants;
  const M = agent.messagesPerConversation;
  const userTurns = Math.floor(M / 2);

  // System prompt sizing
  const sp = pricing.simulator.system_prompt_components;
  const td = pricing.simulator.tool_definitions_tokens;
  const systemPromptBase =
    agent.systemPromptTokens > 0
      ? agent.systemPromptTokens
      : sp.base_persona_tokens
        + sp.tenant_hierarchical_context_tokens
        + (agent.filesCount > 0 ? sp.files_context_prompt_tokens_per_file * Math.min(agent.filesCount, 50) : 0);

  let toolDefTokens = 0;
  if (agent.filesCount > 0) toolDefTokens += td.knowledge_search + td.read_file;
  if (agent.webSearchEnabled) toolDefTokens += td.web_search;
  if (agent.insightsToolsBound) toolDefTokens += td.insights_tools_bundle;

  const prefixTokens = systemPromptBase + toolDefTokens;
  const avgPairTokens = agent.avgUserMessageTokens + agent.avgAssistantTokens;

  // Per-conversation iteration (captures history accumulation)
  let totalUncachedInput = 0;
  let totalCachedReadInput = 0;
  let totalCacheWriteInput = 0;
  let totalOutput = 0;
  let inputTokensRunningSum = 0;

  // Long-context tier (Gemini 3.1 Pro, Claude Haiku 5.5). The ledger charges the higher tier
  // when an INDIVIDUAL call crosses the threshold, so the decision is per
  // turn — applying it to a monthly aggregate would tier a month of small
  // calls the invoice bills at the standard rate. Models without a tier take
  // the identical path as before: hasLongCtx is false and every token lands
  // in the standard bucket.
  const lcInputRate = model.long_context_input_per_million ?? null;
  const lcOutputRate = model.long_context_output_per_million ?? null;
  const lcCacheReadRate = model.long_context_cached_input_per_million ?? null;
  const lcCacheWriteRate = model.long_context_cache_write_per_million ?? null;
  const lcThreshold = model.long_context_threshold_tokens ?? 200_000;
  const hasLongCtx = [lcInputRate, lcOutputRate, lcCacheReadRate, lcCacheWriteRate]
    .some(rate => rate !== null);
  let lcUncachedInput = 0;
  let lcCachedReadInput = 0;
  let lcCacheWriteInput = 0;
  let lcOutput = 0;
  let lcTurns = 0;

  const ragQueriesPerConv = Math.min(agent.ragQueriesPerConversation, userTurns);
  const ragRetrievedTokens =
    pricing.simulator.embeddings.knowledge_search_chunks_returned
    * pricing.simulator.embeddings.tokens_per_chunk
    + pricing.simulator.embeddings.knowledge_search_envelope_overhead_tokens;

  for (let i = 1; i <= userTurns; i++) {
    const historyTokens = (i - 1) * avgPairTokens;
    const ragInjection = (i <= ragQueriesPerConv) ? ragRetrievedTokens : 0;
    const newUserTokens = agent.avgUserMessageTokens;
    const turnInput = prefixTokens + historyTokens + ragInjection + newUserTokens;
    inputTokensRunningSum += turnInput;

    if (hasLongCtx && turnInput > lcThreshold) {
      lcTurns += 1;
      if (i === 1) {
        // Same first-turn split as the untiered path: the prefix is WRITTEN
        // to cache, not billed as input — at the long-context cache-write
        // rate when the model has one (Haiku 5.5), else the base rate.
        if (model.provider === 'anthropic' && caching.cache_write_multiplier) {
          lcCacheWriteInput += prefixTokens;
          lcUncachedInput += (turnInput - prefixTokens);
        } else {
          lcUncachedInput += turnInput;
        }
      } else {
        const cacheable = prefixTokens + historyTokens;
        const cached = cacheable * cacheHitPct;
        lcCachedReadInput += cached;
        lcUncachedInput += (cacheable - cached) + newUserTokens + ragInjection;
      }
      lcOutput += agent.avgAssistantTokens;
      continue;
    }

    if (i === 1) {
      if (model.provider === 'anthropic' && caching.cache_write_multiplier) {
        totalCacheWriteInput += prefixTokens;
        totalUncachedInput += (turnInput - prefixTokens);
      } else {
        totalUncachedInput += turnInput;
      }
    } else {
      const cacheableTokens = prefixTokens + historyTokens;
      const cachedPortion = cacheableTokens * cacheHitPct;
      const uncachedPrefix = cacheableTokens - cachedPortion;
      totalCachedReadInput += cachedPortion;
      totalUncachedInput += uncachedPrefix + newUserTokens + ragInjection;
    }

    totalOutput += agent.avgAssistantTokens;
  }

  totalUncachedInput *= totalConversations;
  totalCachedReadInput *= totalConversations;
  totalCacheWriteInput *= totalConversations;
  lcUncachedInput *= totalConversations;
  lcCachedReadInput *= totalConversations;
  lcCacheWriteInput *= totalConversations;
  lcOutput *= totalConversations;
  // A component the model does not tier stays at its standard rate, as in
  // the ledger's _resolve_long_context_rates.
  const lcIn = lcInputRate ?? model.input_per_million;
  const lcOut = lcOutputRate ?? model.output_per_million;
  const lcCacheRead = lcCacheReadRate ?? model.cached_input_per_million;
  const lcNote = lcTurns
    ? ` + ${fmtNum(lcUncachedInput)} at the long-context tier ($${lcIn}/M above ${fmtNum(lcThreshold)} input tokens/call)`
    : '';
  totalOutput *= totalConversations;

  // ── Cost lines with receipts ───────────────────────────────────────────────

  const mainAIInput: CostLine = {
    cost: (totalUncachedInput / 1_000_000) * model.input_per_million
        + (lcUncachedInput / 1_000_000) * lcIn,
    formula: `${fmtNum(totalUncachedInput)} uncached input tokens ÷ 1M × $${model.input_per_million}/M (${model.name})${lcNote}`,
  };
  const cacheReadCost = (totalCachedReadInput / 1_000_000) * model.cached_input_per_million
    + (lcCachedReadInput / 1_000_000) * lcCacheRead;
  const wouldHaveBeen = (totalCachedReadInput / 1_000_000) * model.input_per_million
    + (lcCachedReadInput / 1_000_000) * lcIn;
  totalCachedReadInput += lcCachedReadInput;
  const cachedDiscount = wouldHaveBeen - cacheReadCost;
  const lcReadNote = lcTurns && lcCachedReadInput && lcCacheReadRate !== null
    ? ` + ${fmtNum(lcCachedReadInput)} of them at the long-context tier ($${lcCacheRead}/M)`
    : '';
  const mainAICachedRead: CostLine = {
    cost: cacheReadCost,
    formula: `${fmtNum(totalCachedReadInput)} cached input tokens ÷ 1M × $${model.cached_input_per_million}/M${lcReadNote} (vs $${model.input_per_million}/M uncached → saves $${cachedDiscount.toFixed(2)} on this line; ${(cacheHitPct * 100).toFixed(0)}% steady-state hit rate for ${model.provider_name})`,
  };
  // Rate resolved server-side by PricingService, the SAME helper the ledger
  // bills with. Re-deriving input x multiplier here would ignore a per-model
  // cache_creation_per_million override and quote a price the invoice does
  // not charge.
  const cacheWriteRate = model.cache_write_per_million
    ?? model.input_per_million * (caching.cache_write_multiplier ?? 0);
  const lcCacheWrite = lcCacheWriteRate ?? cacheWriteRate;
  const mainAICacheWrite: CostLine = {
    cost: caching.cache_write_multiplier
      ? (totalCacheWriteInput / 1_000_000) * cacheWriteRate
        + (lcCacheWriteInput / 1_000_000) * lcCacheWrite
      : 0,
    formula: caching.cache_write_multiplier
      ? `${fmtNum(totalCacheWriteInput)} cache-write tokens ÷ 1M × $${cacheWriteRate}/M`
        + (lcCacheWriteInput ? ` + ${fmtNum(lcCacheWriteInput)} at the long-context tier ($${lcCacheWrite}/M)` : '')
        + ' (cache write surcharge, same rate the ledger bills)'
      : 'n/a (provider does not charge for cache writes)',
  };
  const mainAIOutput: CostLine = {
    cost: (totalOutput / 1_000_000) * model.output_per_million
        + (lcOutput / 1_000_000) * lcOut,
    formula: `${fmtNum(totalOutput)} output tokens ÷ 1M × $${model.output_per_million}/M`,
  };

  // Web search
  const ws = pricing.simulator.web_search;
  const totalWebCalls = agent.webSearchEnabled
    ? totalConversations * agent.webSearchCallsPerConversation
    : 0;
  const webSearch: CostLine = !agent.webSearchEnabled ? ZERO : {
    cost: (totalWebCalls * ws.extra_input_tokens_per_call / 1_000_000) * model.input_per_million
        + (totalWebCalls * ws.extra_output_tokens_per_call / 1_000_000) * model.output_per_million,
    formula: `${fmtNum(totalWebCalls)} web-search calls × (${ws.extra_input_tokens_per_call} in + ${ws.extra_output_tokens_per_call} out tokens) × model rates`,
  };

  // RAG retrieval & rerank
  const emb = pricing.simulator.embeddings;
  const totalRagQueries = (agent.filesCount > 0) ? totalConversations * ragQueriesPerConv : 0;
  const ragRetrieval: CostLine = totalRagQueries === 0 ? ZERO : {
    cost: (totalRagQueries * emb.knowledge_search_query_tokens / 1_000_000) * emb.input_per_million,
    formula: `${fmtNum(totalRagQueries)} knowledge_search queries × ${emb.knowledge_search_query_tokens} embedding tokens ÷ 1M × $${emb.input_per_million}/M (text-embedding-3-small)`,
  };

  const rerank = pricing.simulator.rag_reranking;
  const rerankModel = lookupModel(pricing, rerank.model);
  const totalRerankCalls = totalRagQueries * rerank.fires_per_search_pct;
  const ragRerank: CostLine = totalRagQueries === 0 ? ZERO : {
    cost: (totalRerankCalls * rerank.input_tokens_per_call / 1_000_000) * rerankModel.input_per_million
        + (totalRerankCalls * rerank.output_tokens_per_call / 1_000_000) * rerankModel.output_per_million,
    formula: `${fmtNum(totalRagQueries)} queries × ${(rerank.fires_per_search_pct * 100).toFixed(0)}% rerank rate × (${rerank.input_tokens_per_call} in + ${rerank.output_tokens_per_call} out) × ${rerankModel.name} rates`,
  };

  // File processing — SPLIT into ONE-TIME setup + monthly RECURRING churn.
  // Setup fires ONCE when files are first uploaded (paid in month 1 only).
  // Recurring fires only on the % of files that actually change each month
  // (most enterprise tenants upload once and barely touch their corpus).
  const cpf = chunksPerFile(pricing, agent.avgFileSizeMb);
  const churnRate = Math.max(0, (agent.fileChurnRatePct ?? 5)) / 100;
  const filesSetup = agent.filesCount * params.numSubTenants;            // all files, once
  const filesChurnPerMonth = filesSetup * churnRate;                     // % per month

  // 1) Embedding
  const enrich = pricing.simulator.file_enrichment;
  const eModel = enrichmentModel(pricing);
  const bc = pricing.simulator.bundle_context_haiku;
  const haiku = haikuModel(pricing);

  const tokensPerFile = cpf * emb.tokens_per_chunk;
  const embRate = emb.input_per_million;
  const fileEmbeddingSetup: CostLine = agent.filesCount === 0 ? ZERO : {
    cost: (filesSetup * tokensPerFile / 1_000_000) * embRate,
    formula: `ONE-TIME setup: ${fmtNum(filesSetup)} files (${agent.filesCount} × ${params.numSubTenants} sub-tenants) × ${cpf} chunks × ${emb.tokens_per_chunk} tok/chunk ÷ 1M × $${embRate}/M (text-embedding-3-small)`,
  };
  const fileEmbeddingRecurring: CostLine = (agent.filesCount === 0 || churnRate === 0) ? ZERO : {
    cost: (filesChurnPerMonth * tokensPerFile / 1_000_000) * embRate,
    formula: `RECURRING: ${fmtNum(filesChurnPerMonth)} files/mo (${(churnRate * 100).toFixed(0)}% monthly churn × ${fmtNum(filesSetup)} files) × ${cpf} chunks × ${emb.tokens_per_chunk} tok/chunk ÷ 1M × $${embRate}/M`,
  };

  // 2) Enrichment (per-chunk Haiku-or-gpt-5.4-mini context generation)
  const batchesPerFile = Math.ceil(cpf / enrich.batch_size_chunks);
  const enrichInputPerBatch = enrich.doc_context_tokens + enrich.per_chunk_input_tokens * enrich.batch_size_chunks;
  const enrichOutputPerBatch = enrich.per_chunk_output_tokens * enrich.batch_size_chunks;
  const enrichBatchesSetup = filesSetup * batchesPerFile;
  const enrichBatchesChurn = filesChurnPerMonth * batchesPerFile;
  const enrichCostPerBatch = (enrichInputPerBatch / 1_000_000) * eModel.input_per_million
                           + (enrichOutputPerBatch / 1_000_000) * eModel.output_per_million;
  const fileEnrichmentSetup: CostLine = agent.filesCount === 0 ? ZERO : {
    cost: enrichBatchesSetup * enrichCostPerBatch,
    formula: `ONE-TIME setup: ${fmtNum(filesSetup)} files × ${batchesPerFile} batches/file × (${enrichInputPerBatch} in + ${enrichOutputPerBatch} out) × ${eModel.name} rates`,
  };
  const fileEnrichmentRecurring: CostLine = (agent.filesCount === 0 || churnRate === 0) ? ZERO : {
    cost: enrichBatchesChurn * enrichCostPerBatch,
    formula: `RECURRING: ${fmtNum(filesChurnPerMonth)} files/mo × ${batchesPerFile} batches/file × (${enrichInputPerBatch} in + ${enrichOutputPerBatch} out) × ${eModel.name} rates`,
  };

  // 3) Bundle context Haiku summary (per file, on add/remove)
  const bundleCostPerFile = (bc.input_per_file_tokens / 1_000_000) * haiku.input_per_million
                          + (bc.output_per_file_tokens / 1_000_000) * haiku.output_per_million;
  const bundleContextSetup: CostLine = agent.filesCount === 0 ? ZERO : {
    cost: filesSetup * bundleCostPerFile,
    formula: `ONE-TIME setup: ${fmtNum(filesSetup)} files × (${bc.input_per_file_tokens} in + ${bc.output_per_file_tokens} out) × Haiku rates`,
  };
  const bundleContextRecurring: CostLine = (agent.filesCount === 0 || churnRate === 0) ? ZERO : {
    cost: filesChurnPerMonth * bundleCostPerFile,
    formula: `RECURRING: ${fmtNum(filesChurnPerMonth)} files/mo × (${bc.input_per_file_tokens} in + ${bc.output_per_file_tokens} out) × Haiku rates`,
  };

  // Title gen
  const titleCfg = pricing.simulator.title_generation;
  const titleCallsPerConv = Math.min(titleCfg.calls_per_conversation, userTurns);
  const titleCalls = totalConversations * titleCallsPerConv;
  const title: CostLine = titleCalls === 0 ? ZERO : {
    cost: (titleCalls * titleCfg.input_tokens_per_call / 1_000_000) * haiku.input_per_million
        + (titleCalls * titleCfg.output_tokens_per_call / 1_000_000) * haiku.output_per_million,
    formula: `${fmtNum(totalConversations)} convs × ${titleCallsPerConv} title calls/conv × (${titleCfg.input_tokens_per_call} in + ${titleCfg.output_tokens_per_call} out) × Haiku rates`,
  };

  // Summary
  const summaryCfg = pricing.simulator.summary_generation;
  const tokensPerConv = M * (agent.avgUserMessageTokens + agent.avgAssistantTokens) / 2;
  const minPower = Math.log2(summaryCfg.min_power_of_2_tokens);
  const maxPower = Math.floor(Math.log2(Math.max(tokensPerConv, summaryCfg.min_power_of_2_tokens)));
  const summariesPerConv = Math.max(0, maxPower - minPower + 1);
  const totalSummaryCalls = totalConversations * summariesPerConv;
  const summary: CostLine = summariesPerConv === 0 ? ZERO : {
    cost: (totalSummaryCalls * summaryCfg.input_tokens_per_call / 1_000_000) * haiku.input_per_million
        + (totalSummaryCalls * summaryCfg.output_tokens_per_call / 1_000_000) * haiku.output_per_million,
    formula: `${fmtNum(totalConversations)} convs × ${summariesPerConv} summaries/conv (power-of-2 token thresholds from ${summaryCfg.min_power_of_2_tokens} up to ${fmtNum(tokensPerConv)} avg tok/conv) × (${summaryCfg.input_tokens_per_call} in + ${summaryCfg.output_tokens_per_call} out) × Haiku rates`,
  };

  // Per-agent monthly RECURRING cost (excludes one-time setup)
  const totalCost = mainAIInput.cost + mainAICachedRead.cost + mainAICacheWrite.cost + mainAIOutput.cost
    + webSearch.cost + ragRetrieval.cost + ragRerank.cost
    + fileEmbeddingRecurring.cost + fileEnrichmentRecurring.cost + bundleContextRecurring.cost
    + title.cost + summary.cost;

  const allInputTokens = totalUncachedInput + totalCachedReadInput + totalCacheWriteInput;
  const effectiveCacheHitPct = allInputTokens > 0 ? totalCachedReadInput / allInputTokens : 0;

  const tenantActiveUsers = agent.activeUsersPerMonth * params.numSubTenants;

  return {
    agentId: agent.id,
    agentName: agent.name,
    totalConversations,
    totalUserMessages: totalConversations * userTurns,
    totalAssistantMessages: totalConversations * userTurns,
    mainAIInput, mainAICachedRead, mainAICacheWrite, mainAIOutput,
    webSearch, ragRetrieval, ragRerank,
    fileEmbeddingSetup, fileEmbeddingRecurring,
    fileEnrichmentSetup, fileEnrichmentRecurring,
    bundleContextSetup, bundleContextRecurring,
    title, summary,
    totalCost,
    effectiveCacheHitPct,
    avgInputTokensPerTurn: userTurns > 0 ? inputTokensRunningSum / userTurns : 0,
    costPerConversation: totalConversations > 0 ? totalCost / totalConversations : 0,
    costPerActiveUser: tenantActiveUsers > 0 ? totalCost / tenantActiveUsers : 0,
  };
}

// ── Top-level calculation ────────────────────────────────────────────────────

export function calculateAll(params: SimulationParams, pricing: PricingPayload): CostBreakdown {
  if (params.agents.length === 0) {
    const z: CostLine = { cost: 0, formula: 'No agent configured yet.' };
    return {
      agents: [],
      insights: z, flows: z,
      dbStorage: z, dbCompute: z, gcsStorage: z, gcsOps: z, gcsEgress: z,
      computeBase: z, computePerUser: z, computeConcurrency: z, redis: z,
      totalAICost: 0, totalInfrastructureCost: 0, grandTotal: 0,
      totalSetupCost: 0, firstMonthTotal: 0,
      totalActiveUsers: 0, totalConversations: 0, peakConcurrentUsers: 0,
      pricingVersion: pricing.config_version,
      modelsVersion: pricing.models_version,
    };
  }

  const agentBreakdowns = params.agents.map(a => calculateAgentCost(a, params, pricing));

  const allAgentConversations = agentBreakdowns.reduce((s, a) => s + a.totalConversations, 0);
  const allAgentMessages = agentBreakdowns.reduce((s, a) => s + a.totalUserMessages + a.totalAssistantMessages, 0);

  // Insights — each day a conversation changed, ONE call answers all of the
  // organization's templates (template_runs.py); its size grows per template.
  let insights: CostLine = ZERO;
  if (params.insightsEnabled && allAgentConversations > 0 && params.insightTemplatesPerTenant > 0) {
    const ins = pricing.simulator.insights_extraction;
    const model = lookupModel(pricing, ins.model);
    const activeDays = ins.active_days_per_conversation_per_month ?? 5;
    const templates = params.insightTemplatesPerTenant;
    const totalCalls = allAgentConversations * activeDays;
    const inPerCall = ins.input_tokens_per_call + templates * ins.input_tokens_per_template;
    const outPerCall = ins.output_tokens_per_call + templates * ins.output_tokens_per_template;
    insights = {
      cost: (totalCalls * inPerCall / 1_000_000) * model.input_per_million
          + (totalCalls * outPerCall / 1_000_000) * model.output_per_million,
      formula: `${fmtNum(allAgentConversations)} convs × ${activeDays} active days/mo = ${fmtNum(totalCalls)} ${model.name} calls/mo, each answering ${templates} templates × (${inPerCall} in + ${outPerCall} out)`,
    };
  }

  // Flows
  let flows: CostLine = ZERO;
  if (params.flowsEnabled && params.agents.length > 0) {
    const flowModel = lookupModel(pricing, params.agents[0].model);
    const totalPlatformUsers = params.agents.reduce((s, a) => s + a.activeUsersPerMonth, 0);
    const flowConvs = totalPlatformUsers * params.flowConversationsPerUserPerMonth * params.numSubTenants;
    const flowMsgPairs = flowConvs * params.avgFlowPhases;
    const flowInputTokens = flowMsgPairs * 200;
    const flowOutputTokens = flowMsgPairs * 600;
    flows = {
      cost: (flowInputTokens / 1_000_000) * flowModel.input_per_million
          + (flowOutputTokens / 1_000_000) * flowModel.output_per_million,
      formula: `${fmtNum(flowConvs)} flow convs × ${params.avgFlowPhases} phases × (200 in + 600 out tokens) × ${flowModel.name} rates`,
    };
  }

  // Voice input is FREE via the browser Web Speech API (useVoiceInput.ts) — no
  // backend transcription cost. The legacy Whisper path exists in the codebase
  // but is not part of the simulator.

  // ── Infrastructure (full GCP-style breakdown) ──────────────────────────────
  const totalActiveUsers = params.agents.reduce((s, a) => s + a.activeUsersPerMonth, 0) * params.numSubTenants;
  const infra = pricing.simulator.infrastructure;

  // Concurrent users — capacity-target model.
  // The slider is the OVER-PROVISIONING percentage (100% = exactly all
  // active users could hit at once; 150% = 50% safety margin for spikes).
  // FLOOR: the base Cloud Run instance already handles `concurrent_included_in_base`
  // (typically 80) for free, so the target never goes below that — even tiny
  // tenants get the full base capacity at no extra cost.
  const concBase = (infra.compute as any).concurrent_included_in_base ?? 80;
  const concPerInst = (infra.compute as any).concurrent_per_instance ?? 80;
  const targetConcurrent = Math.max(
    concBase,
    Math.round(totalActiveUsers * (params.concurrentUsersPct / 100)),
  );
  const peakConcurrentUsers = targetConcurrent;

  // DB storage — chunk growth follows per-agent churn rate (was hardcoded 20%)
  const totalFileChunks = params.agents.reduce((s, a) => {
    return s + a.filesCount * chunksPerFile(pricing, a.avgFileSizeMb);
  }, 0) * params.numSubTenants;
  const avgChurnRate = params.agents.length > 0
    ? params.agents.reduce((s, a) => s + (a.fileChurnRatePct ?? 5), 0) / params.agents.length / 100
    : 0.05;
  const newChunksPerMonth = totalFileChunks * avgChurnRate;

  const totalSummariesPerMonth = agentBreakdowns.reduce((s, a) => {
    const ap = params.agents.find(x => x.id === a.agentId)!;
    const tokensPerConv = ap.messagesPerConversation * (ap.avgUserMessageTokens + ap.avgAssistantTokens) / 2;
    const minP = Math.log2(pricing.simulator.summary_generation.min_power_of_2_tokens);
    const maxP = Math.floor(Math.log2(Math.max(tokensPerConv, pricing.simulator.summary_generation.min_power_of_2_tokens)));
    return s + a.totalConversations * Math.max(0, maxP - minP + 1);
  }, 0);

  const monthlyGrowthBytes =
    allAgentMessages * infra.postgres.bytes_per_message
    + allAgentConversations * infra.postgres.bytes_per_conversation
    + newChunksPerMonth * infra.postgres.bytes_per_embedding_row
    + totalSummariesPerMonth * infra.postgres.bytes_per_summary_row
    + (params.insightsEnabled ? allAgentConversations * 1.5 * infra.postgres.bytes_per_insight_result : 0);
  const accumulatedGB = (monthlyGrowthBytes * DB_RETENTION_MONTHS) / (1024 ** 3);

  const dbStorage: CostLine = {
    cost: accumulatedGB * infra.postgres.storage_per_gb_per_month,
    formula: `${accumulatedGB.toFixed(3)} GB accumulated over ${DB_RETENTION_MONTHS} mo (${fmtNum(allAgentMessages)} msgs × ${infra.postgres.bytes_per_message}B + ${fmtNum(allAgentConversations)} convs × ${infra.postgres.bytes_per_conversation}B + ${fmtNum(newChunksPerMonth)} new chunks × ${infra.postgres.bytes_per_embedding_row}B + ${fmtNum(totalSummariesPerMonth)} summaries × ${infra.postgres.bytes_per_summary_row}B per month) × $${infra.postgres.storage_per_gb_per_month}/GB/mo`,
  };
  const dbCompute: CostLine = {
    cost: infra.postgres.compute_base_per_month,
    formula: `Cloud SQL compute base: $${infra.postgres.compute_base_per_month}/mo (db-g1-small equivalent)`,
  };

  // GCS
  const totalStoredGB = CLOUD_STORAGE_ENABLED
    ? params.agents.reduce((s, a) => s + a.filesCount * a.avgFileSizeMb, 0) * params.numSubTenants / 1024
    : 0;
  const totalNewFilesPerMonth = params.agents.reduce((s, a) => s + a.filesCount * ((a.fileChurnRatePct ?? 5) / 100), 0) * params.numSubTenants;

  const gcsStorage: CostLine = !CLOUD_STORAGE_ENABLED ? ZERO : {
    cost: totalStoredGB * infra.gcs.storage_per_gb_per_month,
    formula: `${totalStoredGB.toFixed(3)} GB stored (${params.agents.reduce((s, a) => s + a.filesCount * a.avgFileSizeMb, 0).toFixed(1)} MB/tenant × ${params.numSubTenants} sub-tenants ÷ 1024) × $${infra.gcs.storage_per_gb_per_month}/GB/mo`,
  };
  const gcsOps: CostLine = !CLOUD_STORAGE_ENABLED ? ZERO : {
    cost: (totalNewFilesPerMonth / 10_000) * infra.gcs.ops_per_10k_writes,
    formula: `${fmtNum(totalNewFilesPerMonth)} new file writes/mo ÷ 10k × $${infra.gcs.ops_per_10k_writes}`,
  };
  // GCS egress: every RAG injection downloads ~1 file worth of chunks. Approximate as 0.5MB per RAG query.
  const totalRagDownloads = agentBreakdowns.reduce((s, a, i) => {
    const ap = params.agents[i];
    return s + (ap.filesCount > 0 ? a.totalConversations * Math.min(ap.ragQueriesPerConversation, Math.floor(ap.messagesPerConversation / 2)) : 0);
  }, 0);
  // Intra-region Cloud Run ↔ GCS egress is FREE on GCP.
  const egressGB = CLOUD_STORAGE_ENABLED ? (totalRagDownloads * 0.5) / 1024 : 0;
  const egressRate = (pricing.simulator.infrastructure.gcs as any).egress_per_gb ?? 0;
  const gcsEgress: CostLine = !CLOUD_STORAGE_ENABLED || egressRate === 0 ? { cost: 0, formula: `${egressGB.toFixed(3)} GB intra-region GCS egress × $0/GB (free within GCP same region)` } : {
    cost: egressGB * egressRate,
    formula: `${egressGB.toFixed(3)} GB egress × $${egressRate}/GB`,
  };

  // Compute: base + per-user + per-message + concurrency
  const computeBase: CostLine = {
    cost: infra.compute.base_per_month,
    formula: `Cloud Run / app server base: $${infra.compute.base_per_month}/mo`,
  };
  const computePerUser: CostLine = {
    cost: totalActiveUsers * infra.compute.per_active_user_per_month,
    formula: `${fmtNum(totalActiveUsers)} active users × $${infra.compute.per_active_user_per_month}/user/mo`,
  };
  // Cloud Run capacity scaling — REAL pricing from our deploy config
  // (deploy/deploy-cloud.sh: 2 vCPU + 2 GiB, concurrency=80, max=10).
  const costPerInstance = (infra.compute as any).cost_per_extra_instance ?? 137;
  const extraInstances = Math.max(0, Math.ceil((peakConcurrentUsers - concBase) / concPerInst));
  const extraInstanceCost = extraInstances * costPerInstance;
  const msgCost = (allAgentMessages / 1000) * infra.compute.per_1k_messages_per_month;

  const naiveTarget = Math.round(totalActiveUsers * (params.concurrentUsersPct / 100));
  const flooredNote = naiveTarget < concBase
    ? ` (target ${naiveTarget} < ${concBase} floor; base covers it for free)`
    : '';

  const computeConcurrency: CostLine = {
    cost: msgCost + extraInstanceCost,
    formula:
      `${fmtNum(allAgentMessages)} msgs/mo ÷ 1000 × $${infra.compute.per_1k_messages_per_month}/1k = $${msgCost.toFixed(2)} request volume. ` +
      `Capacity target: ${peakConcurrentUsers} concurrent${flooredNote}. ` +
      `Each extra Cloud Run instance (2 vCPU + 2 GiB always-on) handles ${concPerInst} concurrent at $${costPerInstance}/mo. ` +
      `Need ceil(max(0, ${peakConcurrentUsers}-${concBase})/${concPerInst}) = ${extraInstances} extra × $${costPerInstance} = $${extraInstanceCost.toFixed(2)}.`,
  };

  const redis: CostLine = {
    cost: infra.redis.base_per_month,
    formula: `Redis (256MB, allkeys-lru): $${infra.redis.base_per_month}/mo`,
  };

  const totalAICost = agentBreakdowns.reduce((s, a) => s + a.totalCost, 0)
    + insights.cost + flows.cost;
  const totalInfrastructureCost = dbStorage.cost + dbCompute.cost
    + gcsStorage.cost + gcsOps.cost + gcsEgress.cost
    + computeBase.cost + computePerUser.cost + computeConcurrency.cost
    + redis.cost;
  // ONE-TIME setup costs (paid only in month 1; do NOT recur monthly).
  const totalSetupCost = agentBreakdowns.reduce(
    (s, a) => s + a.fileEmbeddingSetup.cost + a.fileEnrichmentSetup.cost + a.bundleContextSetup.cost,
    0,
  );
  const grandTotal = totalAICost + totalInfrastructureCost;

  return {
    agents: agentBreakdowns,
    insights, flows,
    dbStorage, dbCompute,
    gcsStorage, gcsOps, gcsEgress,
    computeBase, computePerUser, computeConcurrency,
    redis,
    totalAICost,
    totalInfrastructureCost,
    grandTotal,
    totalSetupCost,
    firstMonthTotal: grandTotal + totalSetupCost,
    totalActiveUsers,
    totalConversations: allAgentConversations,
    peakConcurrentUsers,
    pricingVersion: pricing.config_version,
    modelsVersion: pricing.models_version,
  };
}

// ── Defaults ─────────────────────────────────────────────────────────────────

export function defaultAgent(index: number, pricing?: PricingPayload): AgentProfile {
  const preferredModelId = 'claude-sonnet-5';
  const defaultModelId = pricing?.models.some(m => m.id === preferredModelId)
    ? preferredModelId
    : pricing?.models[0]?.id ?? preferredModelId;
  /* Defaults calibrated from the real 'JewishPro' agent on the super-admin
     tenant (146 user messages + 139 assistant messages sampled). User and
     assistant averages are computed STRICTLY separately from the messages
     table, never mixed. See scripts/calibrate_defaults_from_jewish_pro.py. */
  return {
    id: crypto.randomUUID(),
    name: `Agent ${index + 1}`,
    model: defaultModelId,
    activeUsersPerMonth: 100,
    conversationsPerUserPerMonth: 10,
    messagesPerConversation: 8,
    avgUserMessageTokens: 15,    // JewishPro avg user message (sender_type='user' only)
    avgAssistantTokens: 457,     // JewishPro avg assistant response (sender_type='assistant', completion_tokens)
    filesCount: 10,
    avgFileSizeMb: 2,
    fileChurnRatePct: 5,
    ragQueriesPerConversation: 2,
    webSearchEnabled: false,
    webSearchCallsPerConversation: 1,
    insightsToolsBound: false,
    systemPromptTokens: 3024,    // JewishPro system prompt tokens (tiktoken cl100k_base)
    conversationSessionMinutes: 15,
  };
}

export function defaultParams(_pricing?: PricingPayload): SimulationParams {
  return {
    numSubTenants: 1,
    agents: [],
    insightsEnabled: false,
    insightTemplatesPerTenant: 5,
    flowsEnabled: false,
    flowConversationsPerUserPerMonth: 2,
    avgFlowPhases: 5,
    concurrentUsersPct: 100,
  };
}

// Fixed values — data accumulates forever in this platform, and cloud file
// storage is always on. These used to be UI sliders; removed because they
// aren't actually configurable in the platform.
const DB_RETENTION_MONTHS = 12;
const CLOUD_STORAGE_ENABLED = true;

export function formatCurrency(value: number): string {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(2)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(2)}K`;
  if (value >= 1) return `$${value.toFixed(2)}`;
  if (value > 0) return `$${value.toFixed(4)}`;
  return '$0.00';
}

export { lookupModel };
